import { createApi } from "@reduxjs/toolkit/query/react";

import type { IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";
import districtsApi from "../districts/districts";
import projectsApi from "../projects/projects";
import { safeInvalidateTags } from "../safe-invalidate-tags";

import type {
  ICreateDistrictMutation,
  IPlatformDistrict,
  IUpdateDistrictMutation,
} from "./interfaces/platform-districts.interface";

const platformDistrictsApi = createApi({
  reducerPath: "platformDistrictsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.PlatformDistricts, ApiTags.Districts],
  endpoints: (builder) => ({
    createPlatformDistrict: builder.mutation<IPlatformDistrict, ICreateDistrictMutation>({
      query: ({ body }) => ({
        url: "/platform/districts",
        method: "POST",
        body,
      }),
      invalidatesTags: safeInvalidateTags<ApiTags.PlatformDistricts | ApiTags.Districts, unknown, unknown, ICreateDistrictMutation>([
        { type: ApiTags.PlatformDistricts, id: "LIST" }, 
      ]),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
          dispatch(
            districtsApi.endpoints.getDistricts.initiate(undefined, {
              forceRefetch: true,
            }),
          );
        } catch (error) {
          console.error("Failed to create platform district:", error);
        }
      },
      transformResponse: (result: IApiResponse<IPlatformDistrict>) => result.data,
    }),
    updatePlatformDistrict: builder.mutation<void, IUpdateDistrictMutation>({
      query: ({ districtId, body }) => ({
        url: `/platform/districts/${districtId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: safeInvalidateTags<ApiTags.PlatformDistricts | ApiTags.Districts, unknown, unknown, IUpdateDistrictMutation>(
        (_result, _error, arg) => [
          { type: ApiTags.PlatformDistricts, id: "LIST" },
          { type: ApiTags.PlatformDistricts, id: arg.districtId },
        ],
      ),
      async onQueryStarted({ districtId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
          dispatch(
            projectsApi.util.invalidateTags([
              {
                type: ApiTags.Projects,
                id: `${districtId}-sync-shovels`,
              },
            ]),
          );
          dispatch(
            districtsApi.endpoints.getDistricts.initiate(undefined, {
              forceRefetch: true,
            }),
          );
        } catch (error) {
          console.error("Failed to update platform district:", error);
        }
      },
    }),
  }),
});

export const {
  useCreatePlatformDistrictMutation,
  useUpdatePlatformDistrictMutation,
} = platformDistrictsApi;

export default platformDistrictsApi;
